import {lazy, Suspense} from 'react'
import {useParams, useSearchParams, Link} from 'react-router'
import {useQuery} from '@tanstack/react-query'
import {getMetricHistory} from '../../api/metrics.js'
import {TimespanToggle} from '../../components/timespan-toggle/timespan-toggle.jsx'
import styles from './metric-history.module.scss'

const LineChart = lazy(() =>
    import('../../components/line-chart/line-chart.jsx').then(m => ({default: m.LineChart}))
)

const RANGES = ['7d', '30d', '90d', '365d']

export function MetricHistory() {
    const {metricId} = useParams()
    const [params, setParams] = useSearchParams()
    const range = RANGES.includes(params.get('range')) ? params.get('range') : '7d'

    const {data, isLoading, error, refetch} = useQuery({
        queryKey: ['metrics', metricId, range],
        queryFn: () => getMetricHistory(metricId, range),
        staleTime: 5 * 60_000
    })

    const setRange = (next) => {
        const newParams = new URLSearchParams(params)
        newParams.set('range', next)
        setParams(newParams, {replace: true})
    }

    //buckets before the metric's first sample carry `v: null`, and a range that ends in a
    //gap would otherwise render the placeholder as 0.0 — take the newest bucket that has a value
    const samples = data?.samples || []
    let latest
    for (let i = samples.length - 1; i >= 0 && !latest; i--) {
        if (Number.isFinite(samples[i].v))
            latest = samples[i]
    }

    return (
        <div className={styles.screen}>
            <div className={styles.header}>
                <Link to="/" className={styles.back} aria-label="Back to dashboard">
                    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.4">
                        <path d="M15 6l-6 6 6 6"/>
                    </svg>
                </Link>
                <div>
                    <p className={styles.eyebrow}>Metric</p>
                    <h1 className={styles.title}>{metricId}</h1>
                </div>
            </div>

            <TimespanToggle value={range} options={RANGES} onChange={setRange}/>

            <div className={styles.heroBlock}>
                <p className={styles.heroLabel}>Latest reading</p>
                <output className={styles.heroValue}>
                    {latest ? Number(latest.v).toFixed(1) : '—'}
                </output>
                <p className={styles.heroMeta}>
                    {latest?.t ? new Date(latest.t).toLocaleString() : ''}
                </p>
            </div>

            <div className={styles.chartArea}>
                {isLoading && <div className={styles.chartSkeleton} aria-busy="true"/>}
                {error && (
                    <div className={styles.error}>
                        <p>Couldn't load metric history.</p>
                        <button type="button" onClick={() => refetch()}>Retry</button>
                    </div>
                )}
                {!isLoading && !error && data?.samples?.length === 0 && (
                    <p className={styles.empty}>No samples in this range.</p>
                )}
                {!isLoading && !error && data?.samples?.length > 0 && (
                    <Suspense fallback={<div className={styles.chartSkeleton} aria-busy="true"/>}>
                        <LineChart samples={data.samples} label={metricId}/>
                        <p className={styles.chartHint}>Tap or drag the chart to read a value</p>
                    </Suspense>
                )}
            </div>
        </div>
    )
}
